import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  AlertCircle,
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  CreditCard,
  Loader2,
  Percent,
  ReceiptText,
  RefreshCw,
  RotateCcw,
  X,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { feesApi } from '../api/services.js'
import PageHeader from '../components/PageHeader.jsx'
import {
  formatCurrency,
  formatDate,
  getFeePaymentModeLabel,
  getFeePaymentStatusLabel,
  isManualPaymentReferenceRequired,
} from '../utils/fee.js'

const PAYMENT_MODES = ['cash', 'cheque', 'bank_transfer', 'upi']
const accountStatusTone = {
  paid: 'bg-green-50 text-green-700 border-green-100',
  partial: 'bg-amber-50 text-amber-700 border-amber-100',
  pending: 'bg-slate-100 text-slate-600 border-slate-200',
  overdue: 'bg-red-50 text-red-600 border-red-100',
}
const paymentStatusTone = {
  completed: 'bg-green-50 text-green-700 border-green-100',
  reversed: 'bg-red-50 text-red-600 border-red-100',
}

const getEntityId = (value) => value?._id || value?.id || value || ''
const getNumber = (value) => Number(value || 0)
const studentNameFromAccount = (account) =>
  [account?.studentId?.firstName, account?.studentId?.lastName].filter(Boolean).join(' ').trim() || account?.studentId?.name || 'Student'
const classLabelFromAccount = (account) =>
  account?.classId?.name
    ? `${account.classId.name}${account.classId.section ? ` - ${account.classId.section}` : ''}`
    : 'No class assigned'
const getInstallmentNetAmount = (item) =>
  Math.max(0, getNumber(item.amount) - getNumber(item.discount) - getNumber(item.concession) + getNumber(item.fine))
const isPaid = (item) => String(item.status || '').toLowerCase() === 'paid'
const isOverdue = (item) => !isPaid(item) && item.dueDate && new Date(item.dueDate) < new Date()
const monthKey = (dateValue) => {
  const date = dateValue ? new Date(dateValue) : null
  if (!date || Number.isNaN(date.getTime())) return 'No due date'
  return new Intl.DateTimeFormat('en-IN', { month: 'long', year: 'numeric' }).format(date)
}

function groupInstallmentsByMonth(installments) {
  const groups = new Map()
  installments.forEach((item) => {
    const key = monthKey(item.dueDate)
    const current = groups.get(key) || {
      key,
      dueDate: item.dueDate || '',
      installments: [],
      gross: 0,
      net: 0,
      paidAmount: 0,
      outstanding: 0,
    }
    const net = getInstallmentNetAmount(item)
    current.installments.push(item)
    current.gross += getNumber(item.amount)
    current.net += net
    current.paidAmount += getNumber(item.paidAmount)
    current.outstanding += isPaid(item) ? 0 : Math.max(0, net - getNumber(item.paidAmount))
    groups.set(key, current)
  })

  return [...groups.values()].sort((left, right) => new Date(left.dueDate || 0) - new Date(right.dueDate || 0))
}

function normalizePaymentFromReceipt(item) {
  return {
    _id: item.paymentId,
    paymentId: item.paymentId,
    amount: item.totals?.thisPayment || item.amount || 0,
    paymentMode: item.paymentMode,
    referenceNumber: item.referenceNumber,
    receiptNumber: item.receiptNumber,
    paidAt: item.receiptDate || item.paymentDate || item.createdAt,
    status: item.status || 'completed',
    refundReference: item.refundReference,
    reversedAt: item.reversedAt,
  }
}

export default function FeeAccountDetailPage() {
  const { accountId } = useParams()
  const navigate = useNavigate()
  const [account, setAccount] = useState(null)
  const [timeline, setTimeline] = useState([])
  const [loading, setLoading] = useState(true)
  const [receipts, setReceipts] = useState([])
  const [loadingReceipts, setLoadingReceipts] = useState(false)
  const [selectedInstallments, setSelectedInstallments] = useState([])
  const [selectedMonthLabels, setSelectedMonthLabels] = useState([])
  const [payModalOpen, setPayModalOpen] = useState(false)
  const [payForm, setPayForm] = useState({
    amount: '',
    paymentMode: 'cash',
    paymentDate: new Date().toISOString().slice(0, 10),
    referenceNumber: '',
    notes: '',
  })
  const [paying, setPaying] = useState(false)
  const [paymentSuccess, setPaymentSuccess] = useState(null)
  const [reversePayment, setReversePayment] = useState(null)
  const [reverseForm, setReverseForm] = useState({ reason: '', refundReference: '' })
  const [reversing, setReversing] = useState(false)

  const fetchAccount = async () => {
    try {
      setLoading(true)
      const [accountResult, timelineResult] = await Promise.allSettled([
        feesApi.getInstallments(accountId),
        feesApi.getFeeTimeline(accountId),
      ])
      if (accountResult.status === 'fulfilled') setAccount(accountResult.value || null)
      else {
        setAccount(null)
        toast.error(accountResult.reason?.response?.data?.message || 'Failed to load fee account')
      }
      if (timelineResult.status === 'fulfilled') setTimeline(Array.isArray(timelineResult.value) ? timelineResult.value : [])
      else setTimeline([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAccount()
    setSelectedInstallments([])
    setSelectedMonthLabels([])
  }, [accountId])

  const uniquePaymentIds = useMemo(() => {
    if (Array.isArray(account?.payments) && account.payments.length > 0) return []
    return [...new Set((account?.installments || []).map((item) => item.paidByPaymentId).filter(Boolean).map(String))]
  }, [account])

  useEffect(() => {
    let active = true
    if (!uniquePaymentIds.length) {
      setReceipts([])
      return undefined
    }

    const loadReceipts = async () => {
      try {
        setLoadingReceipts(true)
        const results = await Promise.allSettled(uniquePaymentIds.map(async (paymentId) => ({
          paymentId,
          receipt: await feesApi.getReceipt(paymentId),
        })))
        if (!active) return
        setReceipts(
          results
            .filter((result) => result.status === 'fulfilled')
            .map((result) => ({ paymentId: result.value.paymentId, ...result.value.receipt }))
            .sort((left, right) => new Date(right.receiptDate || 0) - new Date(left.receiptDate || 0))
        )
      } finally {
        if (active) setLoadingReceipts(false)
      }
    }

    loadReceipts()
    return () => { active = false }
  }, [uniquePaymentIds])

  const studentName = studentNameFromAccount(account)
  const classLabel = classLabelFromAccount(account)
  const installments = account?.installments || []
  const monthGroups = useMemo(() => groupInstallmentsByMonth(installments), [installments])
  const selectedTotal = useMemo(() => {
    return installments
      .filter((item) => selectedInstallments.includes(getEntityId(item)))
      .reduce((sum, item) => sum + Math.max(0, getInstallmentNetAmount(item) - getNumber(item.paidAmount)), 0)
  }, [installments, selectedInstallments])
  const progressPercent = account?.totalAmount ? Math.min(100, ((account.paidAmount || 0) / account.totalAmount) * 100) : 0
  const paymentHistory = useMemo(() => {
    const rawPayments = Array.isArray(account?.payments) && account.payments.length > 0
      ? account.payments
      : receipts.map(normalizePaymentFromReceipt)
    return [...rawPayments].sort((left, right) => new Date(right.paidAt || right.createdAt || 0) - new Date(left.paidAt || left.createdAt || 0))
  }, [account?.payments, receipts])

  useEffect(() => {
    if (!payModalOpen || !account) return
    const fallbackAmount = selectedInstallments.length ? selectedTotal : Number(account.outstanding || 0)
    setPayForm((prev) => ({ ...prev, amount: fallbackAmount > 0 ? String(fallbackAmount) : '' }))
  }, [account, payModalOpen, selectedInstallments.length, selectedTotal])

  const toggleMonth = (group) => {
    const ids = group.installments.filter((item) => !isPaid(item)).map(getEntityId).filter(Boolean)
    if (!ids.length) return
    const allSelected = ids.every((id) => selectedInstallments.includes(id))
    setSelectedInstallments((prev) => allSelected ? prev.filter((id) => !ids.includes(id)) : [...new Set([...prev, ...ids])])
    setSelectedMonthLabels((prev) => allSelected ? prev.filter((label) => label !== group.key) : [...new Set([...prev, group.key])])
  }

  const clearSelections = () => {
    setSelectedInstallments([])
    setSelectedMonthLabels([])
  }

  const openPayModal = () => {
    if (!account || Number(account.outstanding || 0) <= 0) return
    setPaymentSuccess(null)
    setPayForm({
      amount: selectedInstallments.length ? String(selectedTotal || '') : String(account.outstanding || ''),
      paymentMode: 'cash',
      paymentDate: new Date().toISOString().slice(0, 10),
      referenceNumber: '',
      notes: '',
    })
    setPayModalOpen(true)
  }

  const closePayModal = () => {
    setPayModalOpen(false)
    setPaymentSuccess(null)
    setPaying(false)
  }

  const handlePay = async () => {
    const amount = Number(payForm.amount)
    if (!amount || amount <= 0) {
      toast.error('Enter a valid collection amount')
      return
    }
    if (isManualPaymentReferenceRequired(payForm.paymentMode) && !payForm.referenceNumber.trim()) {
      toast.error(`Enter a reference number for ${getFeePaymentModeLabel(payForm.paymentMode)} payments`)
      return
    }

    try {
      setPaying(true)
      const result = await feesApi.recordPayment(accountId, {
        amount,
        paymentMode: payForm.paymentMode,
        paymentDate: payForm.paymentDate,
        referenceNumber: payForm.referenceNumber.trim() || undefined,
        notes: payForm.notes.trim() || undefined,
        installmentIds: selectedInstallments.length ? selectedInstallments : undefined,
      })

      await fetchAccount()
      setPaymentSuccess({
        paymentId: result?.payment?._id || result?.paymentId || null,
        receiptNumber: result?.receiptNumber || result?.payment?.receiptNumber || 'Recorded',
      })
      clearSelections()
      toast.success('Payment recorded successfully')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record payment')
    } finally {
      setPaying(false)
    }
  }

  const openReversePayment = (payment) => {
    setReversePayment(payment)
    setReverseForm({ reason: '', refundReference: '' })
  }

  const handleReversePayment = async () => {
    if (!reverseForm.reason.trim()) {
      toast.error('Reason is required')
      return
    }
    const paymentId = reversePayment?._id || reversePayment?.paymentId || reversePayment?.id
    if (!paymentId) {
      toast.error('Payment could not be identified')
      return
    }
    const requiresRefundReference = reversePayment?.gatewayPaymentId || String(reversePayment?.paymentMode || '').toLowerCase() === 'razorpay'
    if (requiresRefundReference && !reverseForm.refundReference.trim()) {
      toast.error('Refund reference is required for gateway payments')
      return
    }

    try {
      setReversing(true)
      await feesApi.reversePayment(paymentId, {
        reason: reverseForm.reason.trim(),
        refundReference: reverseForm.refundReference.trim() || undefined,
      })
      toast.success('Payment reversed successfully')
      setReversePayment(null)
      await fetchAccount()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reverse payment')
    } finally {
      setReversing(false)
    }
  }

  const openAdjustments = () => {
    const params = new URLSearchParams({ accountId })
    if (selectedInstallments.length) params.set('installmentIds', selectedInstallments.join(','))
    if (selectedMonthLabels.length) params.set('selectedMonthLabels', selectedMonthLabels.join(','))
    navigate(`/fees/discounts?${params.toString()}`)
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fee Account Details"
        subtitle="Inspect monthly installments, timeline, payment history, receipts, and reversals for this fee account."
        chips={[studentName, classLabel, account?.academicYear || 'Academic year unavailable']}
        actions={
          <>
            <button onClick={() => navigate('/fees/accounts')} className="btn-secondary">
              <ArrowLeft className="w-4 h-4" /> Back to Accounts
            </button>
            <button onClick={fetchAccount} className="btn-secondary">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button onClick={openAdjustments} disabled={!account} className="btn-secondary">
              <Percent className="w-4 h-4" /> Adjust Fees
            </button>
            <button onClick={openPayModal} disabled={!account || Number(account.outstanding || 0) <= 0} className="btn-primary">
              <CreditCard className="w-4 h-4" /> Collect Payment
            </button>
          </>
        }
      />

      {loading ? (
        <div className="card p-12 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
        </div>
      ) : !account ? (
        <div className="empty-state card">
          <ReceiptText className="w-10 h-10 text-gray-200" />
          <p className="empty-state-title">Fee account not found</p>
          <p className="empty-state-copy">This student fee account could not be loaded. Return to the accounts page and open another record.</p>
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <StatCard label="Total Fee" value={formatCurrency(account.totalAmount || 0)} note={account.structureId?.name || 'Fee structure'} />
            <StatCard label="Collected" value={formatCurrency(account.paidAmount || 0)} note={`${progressPercent.toFixed(0)}% of gross demand`} tone="text-green-700" />
            <StatCard label="Outstanding" value={formatCurrency(account.outstanding || 0)} note={selectedInstallments.length ? `${selectedInstallments.length} installments selected` : 'No installment selection'} tone="text-red-600" />
            <div className="card p-5">
              <p className="text-xs uppercase tracking-[0.12em] text-gray-400 font-semibold">Status</p>
              <div className={`inline-flex items-center rounded-full border px-3 py-1.5 text-sm font-semibold mt-3 capitalize ${accountStatusTone[account.status] || accountStatusTone.pending}`}>
                {account.status || 'pending'}
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Discount {formatCurrency(account.discountAmount || 0)} · Concession {formatCurrency(account.concessionAmount || 0)} · Fine {formatCurrency(account.fineAmount || 0)}
              </p>
            </div>
          </div>

          <div className="card p-5">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-900">{studentName}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {classLabel}
                  {account.studentId?.admissionNo ? ` • ${account.studentId.admissionNo}` : ''}
                  {account.academicYear ? ` • ${account.academicYear}` : ''}
                </p>
              </div>
              <div className="min-w-[220px]">
                <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                  <span>Collection progress</span>
                  <span className="font-semibold text-gray-700">{progressPercent.toFixed(1)}%</span>
                </div>
                <div className="h-3 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full rounded-full bg-primary-600 transition-all duration-700" style={{ width: `${progressPercent}%` }} />
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_400px]">
            <div className="card p-5">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Monthly Schedule</h3>
                  <p className="text-sm text-gray-500 mt-1">Select a month to collect or adjust the pending installments inside it.</p>
                </div>
                {selectedInstallments.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-primary-50 px-3 py-1 text-sm font-semibold text-primary-700">
                      Selected total: {formatCurrency(selectedTotal)}
                    </span>
                    <button onClick={clearSelections} className="text-sm font-semibold text-gray-500 hover:text-gray-700">Clear</button>
                  </div>
                )}
              </div>

              <div className="mt-5 space-y-3">
                {monthGroups.map((group) => {
                  const unpaidIds = group.installments.filter((item) => !isPaid(item)).map(getEntityId).filter(Boolean)
                  const allSelected = unpaidIds.length > 0 && unpaidIds.every((id) => selectedInstallments.includes(id))
                  const hasOverdue = group.installments.some(isOverdue)
                  const settled = group.outstanding <= 0
                  return (
                    <button
                      key={group.key}
                      onClick={() => toggleMonth(group)}
                      disabled={!unpaidIds.length}
                      className={`w-full rounded-2xl border px-4 py-4 text-left transition-colors ${
                        allSelected
                          ? 'border-primary-300 bg-primary-50/70'
                          : settled
                          ? 'border-gray-100 bg-gray-50'
                          : 'border-gray-100 bg-white hover:border-primary-200 hover:bg-primary-50/40'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl ${
                          settled ? 'bg-green-50 text-green-600' : hasOverdue ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-slate-500'
                        }`}>
                          {settled ? <CheckCircle2 className="w-5 h-5" /> : <CalendarDays className="w-5 h-5" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900 truncate">{group.key}</p>
                              <p className="text-sm text-gray-500 mt-1">
                                {group.installments.length} installment{group.installments.length === 1 ? '' : 's'} · Due {formatDate(group.dueDate)}
                              </p>
                              <div className="flex flex-wrap gap-2 mt-3">
                                {group.installments.map((item) => (
                                  <span key={getEntityId(item)} className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                                    isPaid(item)
                                      ? 'bg-green-50 text-green-700'
                                      : isOverdue(item)
                                      ? 'bg-red-50 text-red-600'
                                      : 'bg-slate-100 text-slate-600'
                                  }`}>
                                    {item.label || item.componentName || 'Installment'}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div className="text-left lg:text-right flex-shrink-0">
                              <p className="text-lg font-bold text-gray-900">{formatCurrency(group.outstanding || group.net)}</p>
                              <p className="text-xs text-gray-400 mt-1">Net {formatCurrency(group.net)} · Paid {formatCurrency(group.paidAmount)}</p>
                              <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold capitalize mt-3 ${
                                settled
                                  ? 'border-green-100 bg-green-50 text-green-700'
                                  : hasOverdue
                                  ? 'border-red-100 bg-red-50 text-red-600'
                                  : 'border-slate-200 bg-slate-100 text-slate-600'
                              }`}>
                                {settled ? 'paid' : hasOverdue ? 'overdue' : 'pending'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="space-y-6">
              <div className="card p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Payment History</h3>
                    <p className="text-sm text-gray-500 mt-1">Receipts and reversal status for recorded payments.</p>
                  </div>
                  {loadingReceipts ? <Loader2 className="w-4 h-4 animate-spin text-gray-300" /> : null}
                </div>

                <div className="mt-5 space-y-3">
                  {!loadingReceipts && paymentHistory.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center">
                      <p className="text-sm font-semibold text-gray-700">No payment history yet</p>
                      <p className="text-xs text-gray-500 mt-1">The first payment recorded on this account will appear here.</p>
                    </div>
                  ) : (
                    paymentHistory.map((item) => {
                      const paymentId = item._id || item.paymentId || item.id
                      const status = String(item.status || 'completed').toLowerCase()
                      const reversed = status === 'reversed'
                      return (
                        <div key={paymentId} className="rounded-2xl border border-gray-100 bg-white px-4 py-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-semibold text-gray-900">{formatCurrency(item.amount || item.totals?.thisPayment || 0)}</p>
                                <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${paymentStatusTone[status] || paymentStatusTone.completed}`}>
                                  {getFeePaymentStatusLabel(status)}
                                </span>
                              </div>
                              <p className="text-sm text-gray-500 mt-1">
                                {getFeePaymentModeLabel(item.paymentMode)}
                                {item.referenceNumber ? ` • ${item.referenceNumber}` : ''}
                                {item.paidAt || item.receiptDate ? ` • ${formatDate(item.paidAt || item.receiptDate)}` : ''}
                              </p>
                              <p className="text-xs text-gray-400 mt-2">{item.receiptNumber || 'Receipt unavailable'}</p>
                              {reversed && (
                                <p className="text-xs text-red-500 mt-2">
                                  Reversed {item.reversedAt ? formatDate(item.reversedAt) : ''}
                                  {item.refundReference ? ` • Refund ${item.refundReference}` : ''}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-col gap-2">
                              <button onClick={() => navigate(`/fees/receipts/${paymentId}`, { state: { accountPath: `/fees/accounts/${accountId}` } })} className="btn-secondary min-h-[36px] px-3 py-1.5 text-xs">
                                <ReceiptText className="w-3.5 h-3.5" /> Receipt
                              </button>
                              {!reversed && (
                                <button onClick={() => openReversePayment(item)} className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-100">
                                  <RotateCcw className="inline w-3.5 h-3.5" /> Reverse
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              </div>

              <div className="card p-5">
                <h3 className="text-lg font-semibold text-gray-900">Fee Timeline</h3>
                <p className="text-sm text-gray-500 mt-1">Recent fee events from the account timeline.</p>
                <div className="mt-5 space-y-3">
                  {timeline.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
                      No timeline events yet.
                    </div>
                  ) : (
                    timeline.map((event, index) => (
                      <div key={event._id || event.id || index} className="flex gap-3 rounded-2xl border border-gray-100 bg-white px-4 py-3">
                        <span className="mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-primary-500" />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900">{event.title || event.type || 'Fee event'}</p>
                          {event.message || event.description ? <p className="mt-1 text-sm text-gray-500">{event.message || event.description}</p> : null}
                          <p className="mt-1 text-xs text-gray-400">{formatDate(event.createdAt || event.date || event.timestamp)}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {payModalOpen && account && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl">
            {paymentSuccess ? (
              <div className="p-8 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-50 text-green-600">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="mt-5 text-xl font-bold text-gray-900">Payment recorded</h3>
                <p className="text-sm text-gray-500 mt-2">Receipt number</p>
                <p className="text-base font-semibold text-primary-700 mt-1">{paymentSuccess.receiptNumber}</p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  {paymentSuccess.paymentId && (
                    <button onClick={() => navigate(`/fees/receipts/${paymentSuccess.paymentId}`, { state: { accountPath: `/fees/accounts/${accountId}` } })} className="btn-primary flex-1">
                      <ReceiptText className="w-4 h-4" /> View Receipt
                    </button>
                  )}
                  <button onClick={closePayModal} className="btn-secondary flex-1">Back to Account</button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Collect Payment</h3>
                    <p className="text-sm text-gray-500 mt-1">{studentName}</p>
                  </div>
                  <button onClick={closePayModal} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                  <div className="rounded-2xl bg-gray-50 px-4 py-4 text-sm">
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-gray-500">Outstanding balance</span>
                      <span className="font-semibold text-red-600">{formatCurrency(account.outstanding || 0)}</span>
                    </div>
                    {selectedInstallments.length > 0 && (
                      <div className="flex items-center justify-between gap-4 mt-3">
                        <span className="text-gray-500">Selected months</span>
                        <span className="font-semibold text-primary-700">{selectedMonthLabels.join(', ') || `${selectedInstallments.length} installments`} • {formatCurrency(selectedTotal)}</span>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Amount (₹) *</label>
                    <input type="number" className="input-field w-full" value={payForm.amount} disabled={selectedInstallments.length > 0} onChange={(event) => setPayForm((prev) => ({ ...prev, amount: event.target.value }))} />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">Payment Mode</label>
                    <div className="flex flex-wrap gap-2">
                      {PAYMENT_MODES.map((mode) => (
                        <button key={mode} onClick={() => setPayForm((prev) => ({ ...prev, paymentMode: mode }))} className={`rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${payForm.paymentMode === mode ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-600 hover:border-primary-200'}`}>
                          {getFeePaymentModeLabel(mode)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Payment Date</label>
                    <input type="date" className="input-field w-full" value={payForm.paymentDate} onChange={(event) => setPayForm((prev) => ({ ...prev, paymentDate: event.target.value }))} />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Reference Number {isManualPaymentReferenceRequired(payForm.paymentMode) ? '*' : '(optional)'}
                    </label>
                    <input className="input-field w-full" value={payForm.referenceNumber} onChange={(event) => setPayForm((prev) => ({ ...prev, referenceNumber: event.target.value }))} placeholder={isManualPaymentReferenceRequired(payForm.paymentMode) ? 'Enter UTR / transaction / instrument number' : 'Reference not needed for cash'} />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
                    <textarea className="input-field w-full h-20 resize-none" value={payForm.notes} onChange={(event) => setPayForm((prev) => ({ ...prev, notes: event.target.value }))} placeholder="Optional notes" />
                  </div>
                </div>

                <div className="flex flex-col gap-3 border-t border-gray-100 px-6 py-5 sm:flex-row sm:justify-end">
                  <button onClick={closePayModal} className="btn-secondary">Cancel</button>
                  <button onClick={handlePay} disabled={paying} className="btn-primary">
                    {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                    Record Payment
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {reversePayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Reverse Payment</h3>
                <p className="text-sm text-gray-500 mt-1">{formatCurrency(reversePayment.amount || reversePayment.totals?.thisPayment || 0)} · {getFeePaymentModeLabel(reversePayment.paymentMode)}</p>
              </div>
              <button onClick={() => setReversePayment(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                Reversal restores outstanding balances linked to this payment. Use this only for genuine correction or refund cases.
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Reason *</label>
                <textarea className="input-field w-full h-24 resize-none" value={reverseForm.reason} onChange={(event) => setReverseForm((prev) => ({ ...prev, reason: event.target.value }))} placeholder="e.g. Duplicate payment, refund issued" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Refund Reference</label>
                <input className="input-field w-full" value={reverseForm.refundReference} onChange={(event) => setReverseForm((prev) => ({ ...prev, refundReference: event.target.value }))} placeholder="Optional for offline payments" />
              </div>
            </div>
            <div className="flex gap-3 justify-end border-t border-gray-100 px-6 py-5">
              <button onClick={() => setReversePayment(null)} className="btn-secondary">Cancel</button>
              <button onClick={handleReversePayment} disabled={reversing} className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2 font-semibold text-white hover:bg-red-700 disabled:opacity-60">
                {reversing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                Reverse Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function StatCard({ label, value, note, tone = 'text-gray-900' }) {
  return (
    <div className="card p-5">
      <p className="text-xs uppercase tracking-[0.12em] text-gray-400 font-semibold">{label}</p>
      <p className={`text-2xl font-bold mt-3 ${tone}`}>{value}</p>
      <p className="text-xs text-gray-400 mt-2">{note}</p>
    </div>
  )
}
